import { Injectable, Injector, createNgModule } from '@angular/core';

import { FormlyConfig } from '@ngx-formly/core';
import { BehaviorSubject, Observable, combineLatest, from, of } from 'rxjs';
import { filter, map, startWith, take } from 'rxjs/operators';

import { FIELD_LOADERS, FieldPayload } from './field-loaders';
import { INITIAL_MAPPERS } from './initial-field-mappers';
import { SKELETON_MAPPERS } from './skeleton-field-mappers';

import { ISPFieldConfig, ISPFormState } from '../../model';
import { TemplateConfig } from '../../types/template/model';
import {
  TypedControl,
  TypedForm,
  getTypedControls,
} from '../../utils/form-preparing';
import { generateConfigs } from '../../utils/formly-configs-generation';

/**
 * This service allow asynchronosly load dynamic-form fields, if needed
 */
@Injectable()
export class LoaderService {
  /** Loaders for all fields in dynamic-form */
  private readonly loaders = FIELD_LOADERS;

  /** All fields mappers, that service have at the momemnt. Initiallized with initial mappers, that always presents in bundle */
  private readonly fieldMappersSubject = new BehaviorSubject(INITIAL_MAPPERS);

  /** All fields mappers, but instead of them, skeletons are used */
  private readonly skeletonMappers = SKELETON_MAPPERS;

  /** All fields mappers */
  get fieldMappers(): typeof INITIAL_MAPPERS {
    return this.fieldMappersSubject.value;
  }

  constructor(
    private readonly injector: Injector,
    private readonly formlyConfig: FormlyConfig,
  ) {}

  /**
   * Load fields by provided fields names
   *
   * @param names - fields to load
   */
  private loadFields$(
    names: TypedControl['type'][],
  ): Observable<FieldPayload[]> {
    return combineLatest(
      names.map(name =>
        from(
          Promise.all([
            this.loaders[name].component(),
            this.loaders[name].module(),
            this.loaders[name].mapper(),
          ]),
        ).pipe(
          map(([component, module, mapper]) => ({
            name,
            component,
            module,
            mapper,
          })),
        ),
      ),
    );
  }

  /**
   * Register loaded fields in formly and update service state
   *
   * @param fields - loaded fields
   */
  private registerFields(fields: FieldPayload[]): void {
    const newMappers = this.fieldMappersSubject.value;
    fields.forEach(payload => {
      // register field module. Field will be registered automaticaly by module imports
      createNgModule(payload.module, this.injector);
      newMappers[payload.name] = payload.mapper;
    });
    this.fieldMappersSubject.next(newMappers);
  }

  /**
   * Get list of unique fields, that used in form
   *
   * @param typedForm
   */
  private getTypedFormUniqueFields(
    typedForm: TypedForm | undefined,
  ): TypedControl['type'][] {
    const controls = getTypedControls(typedForm);

    return Array.from(new Set(controls.map(control => control.type)).values());
  }

  /**
   * Check registered fields and determine fields to load, from provided fields names
   *
   * @param names
   */
  private getFieldsToLoad(
    names: TypedControl['type'][],
  ): TypedControl['type'][] {
    return names.filter(name => !this.formlyConfig.getType(name, false));
  }

  /**
   * Generate configs for formly. If field is not registered,
   * then skeleton congigs will be used. Fields will be loaded and provided lately in this stream
   *
   * @param typedForm
   * @param formState
   * @param templateConfigs
   */
  generateConfigs$(
    typedForm: TypedForm | undefined,
    formState: ISPFormState,
    templateConfigs: TemplateConfig[],
  ): Observable<ISPFieldConfig[]> {
    const uniqueFields = this.getTypedFormUniqueFields(typedForm);
    const fieldsToLoad = this.getFieldsToLoad(uniqueFields);
    const isAllFieldsLoaded = fieldsToLoad.length === 0;

    if (isAllFieldsLoaded) {
      return of(
        generateConfigs(
          typedForm,
          formState,
          templateConfigs,
          this.fieldMappersSubject.value,
        ),
      );
    }

    this.loadFields$(fieldsToLoad).subscribe(payload => {
      this.registerFields(payload);
    });

    return this.fieldMappersSubject.pipe(
      filter(() => this.getFieldsToLoad(uniqueFields).length === 0),
      map(() =>
        generateConfigs(
          typedForm,
          formState,
          templateConfigs,
          this.fieldMappers,
        ),
      ),
      take(1),
      startWith(
        generateConfigs(
          typedForm,
          formState,
          templateConfigs,
          this.skeletonMappers,
        ),
      ),
    );
  }
}
